/**
 * Resolves dotted variable paths (e.g. `car_listing.make.name`) through
 * wrapper variables. A wrapper's children come from either a custom object
 * (whose fields can themselves be wrappers referencing another custom object)
 * or a dynamic options model (always terminal).
 *
 * Both resolve functions return `{ status, chain }` where:
 * - status 'resolved' — every segment resolved; `chain` holds one node per segment
 * - status 'pending'  — a referenced record is not in the store yet; `chain`
 *                       holds the nodes resolved so far
 * - status 'invalid'  — a segment does not exist; `chain` holds the nodes
 *                       resolved so far
 * Every chain node carries a display-ready `resolvedLabel`.
 *
 * `resolvers` must provide `getCustomObjects`, `getDynamicOptions` (store
 * getters) and `toLocale`.
 */

// undefined -> record not loaded yet (pending); null -> segment not found.
function resolveWrapperChild(parent, segment, resolvers) {
  const { getCustomObjects, getDynamicOptions, toLocale } = resolvers;
  if (parent.custom_object) {
    const record = getCustomObjects(parent.custom_object);
    if (!record) return undefined;
    const field = (record.options || []).find(o => o.name === segment);
    if (!field) return null;
    return { ...field, resolvedLabel: toLocale(field.label, field.name) };
  }
  if (parent.model) {
    const record = getDynamicOptions(parent.model);
    if (!record) return undefined;
    const option = (record.options || []).find(o => `${o.value}` === segment);
    if (!option) return null;
    // Dynamic option children take their value type from the wrapper itself
    // and their labels come already localized from the backend.
    return {
      ...option,
      name: `${option.value}`,
      type: parent.type,
      resolvedLabel: option.label,
    };
  }
  return null;
}

export function resolveWrapperPath(wrapper, segments, resolvers) {
  const chain = [];
  let parent = wrapper;
  for (let i = 0; i < segments.length; i += 1) {
    if (!parent.wrapper) return { status: 'invalid', chain };
    const child = resolveWrapperChild(parent, segments[i], resolvers);
    if (child === undefined) return { status: 'pending', chain };
    if (child === null) return { status: 'invalid', chain };
    chain.push(child);
    parent = child;
  }
  return { status: 'resolved', chain };
}

export function resolveVariablePath(variables, path, resolvers) {
  const segments = String(path).split('.');
  const root = (variables || []).find(v => v.name === segments[0]);
  if (!root) return { status: 'invalid', chain: [] };

  const rootNode = {
    ...root,
    resolvedLabel: resolvers.toLocale(root.label, root.name),
  };
  const { status, chain } = resolveWrapperPath(
    rootNode,
    segments.slice(1),
    resolvers
  );
  return { status, chain: [rootNode, ...chain] };
}
